import {getConnectInstanceUrlFromEnv, isConnectDiagnosticsEnabled} from './urls'
import {runConnectDiagnostics} from './runLatencyDiagnostics'
import {logConnectDiagnosticsToSentry} from './logLatencyDiagnostics'
import {diagnosticsLogger, setLogContext} from '../../Utils/ccpLogger'
import {
  DIAGNOSTICS_LOG_MESSAGES,
  DIAGNOSTICS_TRIGGER_ACCEPT,
  type DiagnosticsIdentity,
} from './logAttributes'

export type TriggerConnectDiagnosticsOnAcceptArgs = DiagnosticsIdentity

/**
 * Fire-and-forget Connect diagnostics after a successful call accept.
 * Runs client config + latency + media STUN. Never throws to the accept path.
 */
export const triggerConnectDiagnosticsOnAccept = (
  args: TriggerConnectDiagnosticsOnAcceptArgs = {}
): void => {
  if (!isConnectDiagnosticsEnabled()) return

  const connectInstanceUrl = getConnectInstanceUrlFromEnv()
  const identity: DiagnosticsIdentity = {
    ...args,
    trigger: args.trigger || DIAGNOSTICS_TRIGGER_ACCEPT,
  }

  setLogContext({
    ...(identity.userId ? {userId: identity.userId} : {}),
    ...(identity.userEmail ? {agentEmail: identity.userEmail} : {}),
    ...(identity.agentName ? {agentName: identity.agentName} : {}),
    ...(identity.contactId ? {contactId: identity.contactId} : {}),
    ...(identity.callHeaderId != null ? {callHeaderId: String(identity.callHeaderId)} : {}),
  })

  if (!connectInstanceUrl) {
    diagnosticsLogger.warn(DIAGNOSTICS_LOG_MESSAGES.skipped, {
      callId: identity.callId,
      callHeaderId: identity.callHeaderId,
      contactId: identity.contactId,
      userId: identity.userId,
      userEmail: identity.userEmail,
    })
    return
  }

  diagnosticsLogger.breadcrumb(DIAGNOSTICS_LOG_MESSAGES.starting, {
    callId: identity.callId,
    callHeaderId: identity.callHeaderId,
    contactId: identity.contactId,
    userId: identity.userId,
    userEmail: identity.userEmail,
    connectInstanceUrl,
  })

  void runConnectDiagnostics({
    connectInstanceUrl,
    callId: identity.callId,
    callHeaderId: identity.callHeaderId,
    contactId: identity.contactId,
  })
    .then((report) => {
      if (!report) return
      logConnectDiagnosticsToSentry(report, identity)
    })
    .catch((error) => {
      diagnosticsLogger.error(DIAGNOSTICS_LOG_MESSAGES.threw, {
        callId: identity.callId,
        callHeaderId: identity.callHeaderId,
        contactId: identity.contactId,
        userId: identity.userId,
        userEmail: identity.userEmail,
        error: String(error),
      })
    })
}

/** @deprecated Prefer triggerConnectDiagnosticsOnAccept */
export const triggerLatencyDiagnosticsOnAccept = triggerConnectDiagnosticsOnAccept

export {runConnectDiagnostics, runLatencyDiagnostics} from './runLatencyDiagnostics'
export {logConnectDiagnosticsToSentry, logLatencyDiagnosticsToSentry} from './logLatencyDiagnostics'
export {collectClientConfig} from './clientConfig'
export {runMediaDiagnostics, probeMediaEndpoint, ICE_TIMEOUT_MS} from './media'
export {probeConnectHeartbeat} from './probeConnectHeartbeat'
export type {ConnectHeartbeatProbeReport, ConnectHeartbeatProbeResult} from './probeConnectHeartbeat'
export {
  DIAGNOSTICS_LOG_MESSAGES,
  DIAGNOSTICS_MODULE,
  DIAGNOSTICS_TRIGGER_ACCEPT,
  buildDiagnosticsLogAttributes,
} from './logAttributes'
export {
  getConnectInstanceUrlFromEnv,
  isConnectDiagnosticsEnabled,
  toConnectInstanceOrigin,
} from './urls'
